import {
	ReactElement,
	RefObject,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import {
	ActionListCheckbox,
	DropOverActionList,
	IconButton,
} from "client/Components";
import styles from "./SharePointColumnsVisibility.module.less";
import { saveColumnsVisibility } from "client/Api/Documents/SharePoint/columnsVisibility";
import { debounce, generateTrackingId } from "client/Misc/utils";
import { tryResolveKeyboardKey } from "client/Misc/keyboardUtils";
import { AlertManager, useAlerts } from "client/Hooks";
import { localize } from "client_legacy/ndaw/culture";
import { SharePointColumnId } from "client/Api/Documents/SharePoint/common";
import { Key } from "client/Misc/commonTypes";

export interface ColumnVisibilityState {
	columnId: SharePointColumnId;
	isVisible: boolean;
}

export interface ColumnInitializationModel {
	id: SharePointColumnId;
	name: string;
	isVisibleByDefault: boolean;
	cannotBeHidden: boolean;
}

export type SharePointColumnsVisibilityProps = {
	isMobile: boolean;
	columnsData: readonly ColumnInitializationModel[];
	value: readonly ColumnVisibilityState[];
	onChange: (newValue: readonly ColumnVisibilityState[]) => void;
};

export const SharePointColumnsVisibility = (props: SharePointColumnsVisibilityProps): ReactElement => {
	const {
		isMobile,
		columnsData,
		value,
		onChange,
	} = props;
	const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
	const alertManager: AlertManager = useAlerts();
	const ref: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);
	const visibleIds: number[] = useMemo(
		() => {
			return value.filter(x => x.isVisible).map(x => x.columnId);
		},
		[value]
	);

	const debouncedFireOnChange: (value: readonly ColumnVisibilityState[]) => void = useMemo(
		() => {
			return debounce(
				(data: readonly ColumnVisibilityState[]) => {
					const visibleColumnsIds: number[] = data
						.filter((item) => item.isVisible)
						.map(x => x.columnId);

					saveColumnsVisibility(visibleColumnsIds)
						.catch(e => {
							console.error(e);
							alertManager.showError(localize("_ChangesNotSaved"));
						});
				},
				1200
			);
		},
		[alertManager]
	);

	const changeHandler = (isVisible: boolean, id: number): void => {
		const newValue: readonly ColumnVisibilityState[] = value.map(x => {
			if (x.columnId === id) {
				return {...x, isVisible};
			}
			return x;
		});

		debouncedFireOnChange(newValue);
		onChange(newValue);
	};

	const dismissHandler = (): void => {
		setIsMenuOpen(false);
	};

	useEffect(
		() => {
			const keyDownHandler = (event: KeyboardEvent) => {
				const key: Key | null = tryResolveKeyboardKey(event);
				switch (key) {
					case Key.Escape:
						dismissHandler();
						break;
					default:
						break;
				};
			};

			window.addEventListener("keydown", keyDownHandler);

			return () => {
				window.removeEventListener("keydown", keyDownHandler);
			};
		},
		[]
	);

	const trackingId = "column-visibility";

	return(
		<>
			<div className={styles.buttonContainer} ref={ref}>
				<IconButton
					kind="secondary"
					trackingId={trackingId}
					isActive={isMenuOpen}
					onClick={() => setIsMenuOpen(state => !state)}
					icon="gear"
					size={isMobile ? "normal" : "large"}
				/>
			</div>
			{(isMenuOpen && ref.current) && (
				<DropOverActionList
					target={ref.current}
					onClose={dismissHandler}
					targetAlignment="br"
					contentAlignment="tr"
					isHeightConstrainted
					trackingId={generateTrackingId(trackingId, "__dropover")}
					menuTitle={localize("_Columns")}
				>
					{columnsData.map((item) => (
						<ActionListCheckbox
							key={item.id}
							text={item.name}
							value={visibleIds.includes(item.id) || item.cannotBeHidden}
							isDisabled={item.cannotBeHidden}
							onChange={(isVisible) => changeHandler(isVisible, item.id)}
							trackingId={generateTrackingId(trackingId, `|${item.id}`)}
							shouldNotAutoCloseContainingDropOver
						/>
					))}
				</DropOverActionList>
			)}
		</>
	);
};

SharePointColumnsVisibility.displayName = "SharePointColumnsVisibility";